import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, User, Building2, BriefcaseBusiness, FileText, ReceiptText } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { Avatar } from '@/components/ui/Avatar'
import type {
  ApiContact,
  ApiCompany,
  ApiDeal,
  ApiQuote,
  ApiJob,
  ApiInvoice,
  PaginatedApiResponse,
} from '@/lib/apiTypes'
import { getGlobalSearchPath } from './GlobalSearchModal'

// ─── Shared search data cache ────────────────────────────────────────────────
// Separate from GlobalSearchModal's cache so they don't conflict.

let _contacts: ApiContact[] | null = null
let _companies: ApiCompany[] | null = null
let _deals: ApiDeal[] | null = null
let _quotes: ApiQuote[] | null = null
let _jobs: ApiJob[] | null = null
let _invoices: ApiInvoice[] | null = null
let _loadPromise: Promise<void> | null = null

function readItems<T>(
  result: PromiseSettledResult<{ data: { data: PaginatedApiResponse<T> } }>,
): T[] {
  if (result.status !== 'fulfilled') return []
  return result.value.data.data.items ?? []
}

async function ensureData(): Promise<void> {
  if (_contacts !== null) return
  if (_loadPromise) return _loadPromise
  _loadPromise = (async () => {
    try {
      const [cr, cor, dr, qr, jr, ir] = await Promise.allSettled([
        apiClient.get('/api/contacts', { params: { take: 100 } }),
        apiClient.get('/api/companies', { params: { take: 100 } }),
        apiClient.get('/api/deals', { params: { take: 100 } }),
        apiClient.get('/api/quotes', { params: { take: 100 } }),
        apiClient.get('/api/jobs', { params: { take: 100 } }),
        apiClient.get('/api/invoices', { params: { take: 100 } }),
      ])
      _contacts = readItems<ApiContact>(cr)
      _companies = readItems<ApiCompany>(cor)
      _deals = readItems<ApiDeal>(dr)
      _quotes = readItems<ApiQuote>(qr)
      _jobs = readItems<ApiJob>(jr)
      _invoices = readItems<ApiInvoice>(ir)
    } catch {
      _contacts = _contacts ?? []
      _companies = _companies ?? []
      _deals = _deals ?? []
      _quotes = _quotes ?? []
      _jobs = _jobs ?? []
      _invoices = _invoices ?? []
    }
  })()
  return _loadPromise
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

function matchQ(haystack: string, q: string) {
  return haystack.toLowerCase().includes(q.toLowerCase())
}

function fullName(c?: { firstName: string; lastName: string } | null): string {
  return c ? `${c.firstName} ${c.lastName}`.trim() : ''
}

// ─── Result types ─────────────────────────────────────────────────────────────

type ResultSection = 'contact' | 'company' | 'deal' | 'quote' | 'job' | 'invoice'

interface SearchResult {
  id: string
  section: ResultSection
  label: string
  sub?: string
  path?: string
}

function buildResults(query: string): SearchResult[] {
  if (!query.trim()) return []
  const results: SearchResult[] = []

  for (const c of (_contacts ?? [])) {
    if (results.filter(r => r.section === 'contact').length >= 4) break
    if (matchQ(`${c.firstName} ${c.lastName} ${c.email ?? ''} ${c.phone ?? ''}`, query))
      results.push({ id: c.id, section: 'contact', label: `${c.firstName} ${c.lastName}`, sub: c.email })
  }

  for (const c of (_companies ?? [])) {
    if (results.filter(r => r.section === 'company').length >= 3) break
    if (matchQ(`${c.name} ${c.industry ?? ''}`, query))
      results.push({ id: c.id, section: 'company', label: c.name, sub: c.industry })
  }

  for (const d of (_deals ?? [])) {
    if (results.filter(r => r.section === 'deal').length >= 3) break
    if (matchQ(`${d.name} ${fullName(d.contact)} ${d.company?.name ?? ''}`, query))
      results.push({ id: d.id, section: 'deal', label: d.name, sub: d.company?.name })
  }

  for (const q of (_quotes ?? [])) {
    if (results.filter(r => r.section === 'quote').length >= 3) break
    if (matchQ(`${q.quoteNumber ?? ''} ${fullName(q.contact)} ${q.company?.name ?? ''}`, query))
      results.push({
        id: q.id,
        section: 'quote',
        label: q.quoteNumber ?? `Quote ${q.id.slice(-6).toUpperCase()}`,
        sub: fullName(q.contact) || q.company?.name,
      })
  }

  for (const j of (_jobs ?? [])) {
    if (results.filter(r => r.section === 'job').length >= 3) break
    if (matchQ(`${j.address ?? ''} ${fullName(j.contact)} ${j.company?.name ?? ''}`, query))
      results.push({
        id: j.id,
        section: 'job',
        label: j.address || fullName(j.contact) || 'Job',
        sub: j.status,
      })
  }

  for (const i of (_invoices ?? [])) {
    if (results.filter(r => r.section === 'invoice').length >= 3) break
    if (matchQ(`${i.invoiceNumber} ${fullName(i.contact)} ${i.company?.name ?? ''}`, query))
      results.push({
        id: i.id,
        section: 'invoice',
        label: i.invoiceNumber,
        sub: fullName(i.contact) || i.company?.name,
      })
  }

  return results
}

function sectionLabel(s: ResultSection): string {
  const map: Record<ResultSection, string> = {
    contact: 'Customers',
    company: 'Companies',
    deal: 'Deals',
    quote: 'Quotes',
    job: 'Jobs',
    invoice: 'Invoices',
  }
  return map[s]
}

function ResultIcon({ section }: { section: ResultSection }) {
  const cls = 'w-4 h-4 text-ink-400'
  if (section === 'company') return <Building2 className={cls} />
  if (section === 'deal') return <BriefcaseBusiness className={cls} />
  if (section === 'quote') return <FileText className={cls} />
  if (section === 'job') return <BriefcaseBusiness className={cls} />
  if (section === 'invoice') return <ReceiptText className={cls} />
  return <User className={cls} />
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HeaderSearch() {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [focused, setFocused] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const results = dataReady ? buildResults(debouncedQuery) : []
  const showDropdown = focused && (loading || debouncedQuery.length > 0)

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250)
    return () => clearTimeout(t)
  }, [query])

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1)
  }, [debouncedQuery])

  // Fetch data on first focus
  useEffect(() => {
    if (!focused || dataReady) return
    setLoading(true)
    ensureData()
      .then(() => { setDataReady(true); setLoading(false) })
      .catch(() => { setDataReady(true); setLoading(false) })
  }, [focused, dataReady])

  // Click-outside to dismiss
  useEffect(() => {
    if (!focused) return
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) dismiss()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [focused])

  const dismiss = useCallback(() => {
    setFocused(false)
    setQuery('')
    setDebouncedQuery('')
    setActiveIndex(-1)
    inputRef.current?.blur()
  }, [])

  function navigateTo(result: SearchResult) {
    dismiss()
    navigate(getGlobalSearchPath(result))
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        dismiss()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => Math.min(i + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => Math.max(i - 1, -1))
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault()
        const r = results[activeIndex]
        if (r) navigateTo(r)
      }
    },
    [dismiss, results, activeIndex],
  )

  // Group results by section for display
  const sections = Array.from(new Set(results.map(r => r.section))) as ResultSection[]

  return (
    <div ref={containerRef} className="relative hidden xl:block">
      {/* Input pill */}
      <div
        className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border transition-all ${
          focused
            ? 'bg-white/[0.06] border-white/[0.12] w-72'
            : 'bg-white/[0.03] border-white/[0.06] w-48 hover:bg-white/[0.05] hover:border-white/[0.10]'
        }`}
      >
        <Search className="w-4 h-4 text-ink-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={focused ? 'Search anything...' : 'Search'}
          className="flex-1 min-w-0 bg-transparent text-sm text-ink-100 outline-none placeholder:text-white/40"
          autoComplete="off"
          spellCheck={false}
          aria-label="Search"
          aria-haspopup="listbox"
          aria-expanded={showDropdown}
        />
        {/* X — visible when focused or query non-empty */}
        {(focused || query) && (
          <button
            onMouseDown={e => { e.preventDefault(); dismiss() }}
            aria-label="Clear search"
            className="p-0.5 text-white/30 hover:text-ink-200 transition-colors shrink-0 rounded"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {!focused && !query && (
          <kbd className="inline-flex items-center text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded text-white/40 border border-white/[0.08] font-mono shrink-0">
            ⌘K
          </kbd>
        )}
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div
          role="listbox"
          aria-label="Search results"
          className="absolute top-full mt-2 right-0 w-80 glass-popup rounded-xl overflow-hidden z-[90] animate-fade-up"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
        >
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
            </div>
          )}

          {/* No results */}
          {!loading && debouncedQuery && results.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-ink-400">
                No results for{' '}
                <span className="text-ink-200 font-medium">"{debouncedQuery}"</span>
              </p>
            </div>
          )}

          {/* Hint when focused but empty query */}
          {!loading && !debouncedQuery && (
            <div className="py-6 text-center">
              <p className="text-xs text-white/30">Type to search customers, work, invoices…</p>
            </div>
          )}

          {/* Result sections */}
          {!loading && results.length > 0 &&
            sections.map((section, si) => {
              const sectionResults = results.filter(r => r.section === section)
              const offset = results.findIndex(r => r.section === section)
              return (
                <section key={section} className={si > 0 ? 'border-t border-white/[0.05]' : ''}>
                  <p className="eyebrow px-4 py-2">{sectionLabel(section)}</p>
                  {sectionResults.map((result, idx) => {
                    const globalIdx = offset + idx
                    const isActive = globalIdx === activeIndex
                    return (
                      <button
                        key={`${result.section}:${result.id}`}
                        role="option"
                        aria-selected={isActive}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                        }`}
                        onMouseDown={e => { e.preventDefault(); navigateTo(result) }}
                        onMouseEnter={() => setActiveIndex(globalIdx)}
                      >
                        <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-white/[0.05] border border-white/[0.08]">
                          {section === 'contact'
                            ? <Avatar name={result.label} size="sm" />
                            : <ResultIcon section={result.section} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-ink-100 truncate">{result.label}</p>
                          {result.sub && (
                            <p className="text-xs text-white/40 truncate">{result.sub}</p>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </section>
              )
            })}

          {/* Footer */}
          {!loading && results.length > 0 && (
            <div className="border-t border-white/[0.05] px-4 py-2 flex items-center gap-2">
              <kbd className="text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded text-white/30 border border-white/[0.08] font-mono">↑↓</kbd>
              <span className="text-[10px] text-white/30">navigate</span>
              <kbd className="text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded text-white/30 border border-white/[0.08] font-mono ml-2">↵</kbd>
              <span className="text-[10px] text-white/30">open</span>
              <kbd className="text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded text-white/30 border border-white/[0.08] font-mono ml-2">ESC</kbd>
              <span className="text-[10px] text-white/30">close</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
